import { useFormContext } from 'react-hook-form';
import { patchAppDataOpts, useApplication } from 'merchant-api/unified';
import BusinessDetails, { getBusinessDetailsSchema } from './BusinessDetails';
import { FormattedMessageDiv, FormWrapper, LoadingAnimation, Navigation, useWizardContext } from '@odmonorepo/core-components';
import * as rfc6902 from 'rfc6902';
import * as yup from 'yup';
import { Stack } from 'react-bootstrap';
import { useMutation } from '@tanstack/react-query';
import { ensureSingleItem, initializeField } from '../businessFormHelpers';
import { formatToISODate, formatFromISODate } from '@odmonorepo/odfunctions';
import { hasProveBusinessPrefill } from '../helpers';

const buildBusinessDetailsSchema = (showEstimatedAnnualRevenue: boolean) => {
  const BusinessDetailsSchema = getBusinessDetailsSchema();
  const businessDetailsFields = (BusinessDetailsSchema.fields.businesses as any).innerType.fields;
  return yup.object().shape({
    ...(showEstimatedAnnualRevenue && {
      estimatedAnnualRevenue: yup.number().required(),
    }),
    desiredUseOfFunds: yup.string().required(),
    party: yup.object().shape({
      persons: BusinessDetailsSchema.fields.persons,
      businesses: yup.array().of(
        yup.object().shape(businessDetailsFields)
      ),
    }),
    thirdPartyLenders: BusinessDetailsSchema.fields.thirdPartyLenders,
  });
};

const handleFormRadio = (party, thirdPartyLenders) => {
  party.persons[0].typeData.hasBeneficialOwner = party.persons[0].typeData.hasBeneficialOwner === 'true';
  party.persons[0].typeData.hasManagingPerson = party.persons[0].typeData.hasManagingPerson === 'true';
  if ([null, undefined].includes(thirdPartyLenders[0].existingLenderName)) {
    thirdPartyLenders[0].existingLenderName = '';
  }
  if ([null, undefined].includes(thirdPartyLenders[0].currentBalance)) {
    thirdPartyLenders[0].currentBalance = 0;
  }
  if (party?.persons?.[0]?.typeData?.ownershipPct >= 75) {
    party.persons[0].typeData.hasBeneficialOwner = false;
  }
  delete thirdPartyLenders?.[0]?.stf;
};

const formatData = (data) => {
  if (!data) return data;
  const formattedData = JSON.parse(JSON.stringify(data));

  formattedData.desiredUseOfFunds ??= "";
  formattedData.party.businesses = ensureSingleItem(formattedData.party.businesses, {
    identifiers: [{ type: 'TAX_ID' }],
  });
  initializeField(formattedData?.party?.businesses[0], 'identifiers', 'TAX_ID');

  formattedData.party.persons = ensureSingleItem(formattedData.party.persons, {});
  formattedData.thirdPartyLenders = ensureSingleItem(formattedData.thirdPartyLenders, { competitorName: '', currentBalance: 0, payoffAmount: 0 });

  if (formattedData.party.businesses[0].inceptionDate) {
    formattedData.party.businesses[0].inceptionDate = formatFromISODate(formattedData.party.businesses[0].inceptionDate) || '';
  } else {
    formattedData.party.businesses[0].inceptionDate = '';
  }

  formattedData.party.businesses[0].legalEntityType = formattedData.party.businesses[0].legalEntityType ?? '';

  // The form edits ownership under persons[0].typeData (the legacy Butterfly document shape),
  // but Komodo keeps it in party.ownership[] and silently drops typeData on write. Seed the
  // form's typeData from the canonical ownership entry; formatNewData maps it back on save.
  const ownership = formattedData.party.ownership?.[0];
  const typeData = formattedData.party.persons[0].typeData ?? {};
  formattedData.party.persons[0].typeData = {
    ...typeData,
    ownershipPct: ownership?.ownershipPct ?? typeData.ownershipPct,
    hasManagingPerson: String(ownership?.hasManagingPerson ?? typeData.hasManagingPerson),
    hasBeneficialOwner: String(ownership?.hasBeneficialOwner ?? typeData.hasBeneficialOwner),
  };
  return formattedData;
};

const formatNewData = (applicationData, newData) => {
  let { party, thirdPartyLenders } = newData;
  const inceptionDate = party?.businesses[0]?.inceptionDate;
  if (inceptionDate) {
    newData.party.businesses[0].inceptionDate = formatToISODate(inceptionDate);
  }
  handleFormRadio(party, thirdPartyLenders);

  ['businesses', 'persons'].forEach((field) => {
    const index = applicationData?.data.party?.[field]?.findIndex((data) => data.id === party?.[field][0]?.id);
    party[field] = [...applicationData?.data?.party[field].slice(0, index), party[field][0], ...applicationData?.data?.party[field].slice(index + 1)];
  });

  // Persist the ownership answers where Komodo actually keeps them (party.ownership[]), then
  // drop the form-only typeData so the patch never writes the legacy shape (Komodo ignores it,
  // which left ownership empty and lost the user's answers).
  const answers = party.persons[0].typeData ?? {};
  const existingOwnership = applicationData?.data?.party?.ownership?.[0];
  // No personId/businessId: cached ids can be stale, and Komodo rejects writes to them. The
  // unified data-patch route resolves the refs against the party it just read (rebaseOwnershipRefs).
  const { personId: _personId, businessId: _businessId, ...carriedOwnership } = existingOwnership ?? {};
  party.ownership = [{
    ...carriedOwnership,
    isPrimaryOwner: existingOwnership?.isPrimaryOwner ?? false,
    ownershipPct: Number(answers.ownershipPct),
    hasManagingPerson: answers.hasManagingPerson,
    hasBeneficialOwner: answers.hasBeneficialOwner,
  }];
  delete party.persons[0].typeData;

  const lenderIndex = applicationData?.data?.thirdPartyLenders?.findIndex((data) => data.id === thirdPartyLenders?.[0]?.id);
  newData.thirdPartyLenders = [...applicationData?.data?.thirdPartyLenders.slice(0, lenderIndex), thirdPartyLenders[0], ...applicationData?.data?.thirdPartyLenders.slice(lenderIndex + 1)];

  if (!newData.desiredFundsAvailability) {
      newData.desiredFundsAvailability = 'TWO_TO_THREE_DAYS';
  }
  if ((typeof newData.desiredAmountToBorrow === 'undefined' || newData.desiredAmountToBorrow === null )){
      newData.desiredAmountToBorrow = 0;
  }

  return newData;
};

export const BusinessDetailsStep = () => {
  const { applicationData, isLoadingApplication } = useApplication();

  if (isLoadingApplication)
    return (
      <div className="mx-auto">
        <LoadingAnimation />
      </div>
    );

  const showEstimatedAnnualRevenue =
    applicationData?.data?.estimatedAnnualRevenue === null ||
    applicationData?.data?.estimatedAnnualRevenue === undefined ||
    applicationData?.data?.estimatedAnnualRevenue === 0 ||
    (typeof applicationData?.data?.estimatedAnnualRevenue === 'string' && applicationData?.data?.estimatedAnnualRevenue === '');

  const schema = buildBusinessDetailsSchema(showEstimatedAnnualRevenue);
  const defaultValues = formatData(applicationData?.data);
  const review = hasProveBusinessPrefill(applicationData ?? undefined);

  return (
    <FormWrapper schema={schema} defaultValues={defaultValues}>
      <Stack gap={review ? 5 : 6} className={review ? 'w-100 px-2 px-md-0' : undefined} style={{ maxWidth: '600px', margin: 'auto' }}>
        <FormattedMessageDiv className={review ? 'h3 text-center mb-0' : 'h3 text-center'} id={review ? 'businessReview.header' : 'renewalApp.step.1'} />
        <BusinessDetailsForm applicationData={applicationData} showEstimatedAnnualRevenue={showEstimatedAnnualRevenue} review={review} />
      </Stack>
    </FormWrapper>
  );
};

type BusinessDetailsFormProps = {
  applicationData: any;
  showEstimatedAnnualRevenue: boolean;
  review?: boolean;
};

const BusinessDetailsForm = ({ applicationData, showEstimatedAnnualRevenue, review }: BusinessDetailsFormProps) => {
  const { getValues } = useFormContext();
  const { nextStep } = useWizardContext();
  const { mutateAsync } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => nextStep(),
    onError: (error) => {
      throw error;
    },
  });

  const onNext = async () => {
    const newData = formatNewData(applicationData, getValues());
    const patches = rfc6902.createPatch(applicationData?.data, newData);
    // Komodo rejects per-element ownership inserts ("Cannot modify protected field ...
    // /party/ownership/0/personId") but accepts replacing the whole array, so collapse the
    // first-write diff (add /party/ownership/0) into a whole-array write.
    const ownershipOps = patches.filter((p) => p.path.startsWith('/party/ownership'));
    const finalPatches = ownershipOps.some((p) => p.op === 'add')
      ? [
          ...patches.filter((p) => !p.path.startsWith('/party/ownership')),
          {
            op: applicationData?.data?.party?.ownership ? 'replace' : 'add',
            path: '/party/ownership',
            value: newData.party.ownership,
          } as rfc6902.Operation,
        ]
      : patches;
    await mutateAsync({ id: applicationData.id, patches: finalPatches });
  };

  return (
    <>
      <BusinessDetails showEstimatedAnnualRevenue={showEstimatedAnnualRevenue} review={review}>
        <Navigation stacked handleNext={onNext} />
      </BusinessDetails>
      <FormattedMessageDiv className="fs-8" id="businessDetails.disclaimer" />
    </>
  );
};

export default BusinessDetailsStep;
